/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

type StorageTypeName = 'sessionStorage' | 'localStorage'

type StorageType = typeof sessionStorage | typeof localStorage

export abstract class StorageUtility {
  private storageType: StorageType

  constructor(type: StorageTypeName) {
    this.storageType = window[type]
  }

  save(
    key: string,
    val: any,
    ttl: number | 'disabled' = 'disabled',
    beforeSave = (current: Record<string, any>): void => {},
  ): Record<string, any> {
    beforeSave(this.getSafe(key))

    this.storageType.setItem(
      key,
      JSON.stringify({ value: val, ttl, timestamp: Date.now() }),
    )

    return this.getSafe(key)
  }

  protected get(key: string): string | null {
    return this.storageType.getItem(key)
  }

  getSafe(key: string): Record<string, any> {
    const result = this.get(key)
    if (result === null) {
      return {}
    }

    const parsedResult = JSON.parse(result)

    if (this.checkExpired(parsedResult)) {
      this.remove(key)
      return {}
    }

    return parsedResult
  }

  add(key: string, val: any): void {
    this.save(key, {
      ...this.getSafe(key).value,
      ...val,
    })
  }

  protected checkExpired(data: Record<string, any>): boolean {
    const { ttl, timestamp } = data
    return ttl !== 'disabled' && Date.now() - timestamp >= ttl
  }

  remove(key: string): void {
    this.storageType.removeItem(key)
  }

  clear(): void {
    this.storageType.clear()
  }
}

export class SessionStorageUtility extends StorageUtility {
  constructor() {
    super('sessionStorage')
  }
}

export class LocalStorageUtility extends StorageUtility {
  constructor() {
    super('localStorage')
  }
}

export class LocalStorageStaticUtility extends LocalStorageUtility {
  private static instance: LocalStorageStaticUtility

  constructor() {
    if (!LocalStorageStaticUtility.instance) {
      super()
      LocalStorageStaticUtility.instance = this
    }
    return LocalStorageStaticUtility.instance
  }

  static setItem<T>(name: string, data: T): void {
    LocalStorageStaticUtility.instance.save(name, data)
  }

  static getItem(name: string): Record<string, any> | null {
    return LocalStorageStaticUtility.instance.getSafe(name)
  }
}
